/**
 * Where a storey sits in its building, shared by extracted design facts,
 * cropped drawings and FFU facit. Printed identity (`label`, `planNumbers`)
 * lives next to it on each consumer; this is the resolved placement.
 */

import { z } from 'zod';

/** Storey kinds that can carry their own index besides a full storey. */
export const SPECIAL_STOREY_KINDS = [
  'attic',
  'technical',
  'mezzanine',
  'split_level',
] as const;

export type SpecialStoreyKind = (typeof SPECIAL_STOREY_KINDS)[number];

export const specialStoreyKindSchema = z.enum(SPECIAL_STOREY_KINDS);

const storeyIndexSchema = z
  .number()
  .int()
  .describe('0 on the entrance storey, negative below, positive above.');

const singleStoreyLevelSchema = z
  .object({ kind: z.literal('single'), index: storeyIndexSchema })
  .strict();

const rangeStoreyLevelSchema = z
  .object({
    kind: z.literal('range'),
    fromIndex: storeyIndexSchema,
    toIndex: storeyIndexSchema,
  })
  .strict()
  .refine((level) => level.fromIndex < level.toIndex, {
    message: 'fromIndex must be below toIndex',
  });

const typicalStoreyLevelSchema = z
  .object({ kind: z.literal('typical') })
  .strict();

const specialStoreyLevelSchema = <K extends SpecialStoreyKind>(kind: K) =>
  z
    .object({ kind: z.literal(kind), index: storeyIndexSchema.nullable() })
    .strict();

const roofStoreyLevelSchema = z.object({ kind: z.literal('roof') }).strict();

/**
 * Resolved storey placement relative to the entrance (index 0).
 *
 * - `single`: one full storey.
 * - `range`: consecutive storeys drawn once ("Plan 11-14" once the entrance is known).
 * - `typical`: repeating normal floor whose positions are not asserted (facit `"*"`).
 * - `attic` (vind), `technical` (teknikvåning / fläktrum on top),
 *   `mezzanine` (entresol, `index` = the storey it sits in), `split_level`
 *   (suterräng): special storeys; `index` null when not placed.
 * - `roof`: a roof plan (takplan) — a view, not an occupiable storey.
 *
 * Consumers that store "not placed yet" use `StoreyLevel | null`.
 */
export const storeyLevelSchema = z.discriminatedUnion('kind', [
  singleStoreyLevelSchema,
  rangeStoreyLevelSchema,
  typicalStoreyLevelSchema,
  specialStoreyLevelSchema('attic'),
  specialStoreyLevelSchema('technical'),
  specialStoreyLevelSchema('mezzanine'),
  specialStoreyLevelSchema('split_level'),
  roofStoreyLevelSchema,
]);

export type StoreyLevel = z.infer<typeof storeyLevelSchema>;

export type StoreyLevelKind = StoreyLevel['kind'];

/**
 * Storey kind as AI extractors emit it (flat enum, OpenAI strict mode forbids
 * unions). Narrowed to {@link StoreyLevel} after parse.
 */
export const AI_STOREY_KINDS = [
  'regular',
  'typical',
  ...SPECIAL_STOREY_KINDS,
  'roof',
] as const;

export type AiStoreyKind = (typeof AI_STOREY_KINDS)[number];

export const aiStoreyKindSchema = z.enum(AI_STOREY_KINDS);

/**
 * Which storeys a plan drawing shows.
 *
 * - `specific`: the storeys in {@link StoreyLevel} (one storey, a printed
 *   range, an attic, a takplan).
 * - `remaining`: a normalplan — every storey of the building that has no
 *   specific plan of its own. Facit `storeyIndex: "*"`. It does not need a
 *   storey row of its own; the storeys it covers are resolved against the
 *   specific plans next to it.
 */
export const planCoverageSchema = z.discriminatedUnion('kind', [
  z
    .object({ kind: z.literal('specific'), level: storeyLevelSchema })
    .strict()
    .refine((coverage) => coverage.level.kind !== 'typical', {
      message: 'A normalplan is `remaining`, not a specific typical level',
    }),
  z.object({ kind: z.literal('remaining') }).strict(),
]);

export type PlanCoverage = z.infer<typeof planCoverageSchema>;

/**
 * Which drawing a storey is built from.
 *
 * - `own`: a specific plan shows it.
 * - `normalplan`: no plan of its own; the normalplan (`remaining` coverage)
 *   stands for it. Only for storeys above the entrance that are not an attic.
 * - `data_gap`: the storey exists but no drawing can be used for it — the
 *   entrance, an attic or a storey below ground without its own plan, or any
 *   storey when there is no normalplan. Stated, never guessed.
 *
 * Consumers that do not know leave it unset; it then resolves from the plans
 * (see `resolveStoreyDrawings`).
 */
export const STOREY_DRAWING_STATES = ['own', 'normalplan', 'data_gap'] as const;

export type StoreyDrawing = (typeof STOREY_DRAWING_STATES)[number];

export const storeyDrawingSchema = z.enum(STOREY_DRAWING_STATES);

/**
 * Storey count of a building or part. `at_least` when the documents only
 * support a minimum (detaljplan "minst III"). Convention: storeys from the
 * entrance up, attic and technical top floors included; storeys below ground,
 * entresol and takplan not (see `countStoreys`).
 */
export const storeyCountSchema = z.discriminatedUnion('kind', [
  z
    .object({ kind: z.literal('exact'), value: z.number().int().nonnegative() })
    .strict(),
  z
    .object({
      kind: z.literal('at_least'),
      value: z.number().int().nonnegative(),
    })
    .strict(),
]);

export type StoreyCount = z.infer<typeof storeyCountSchema>;
